import React, { useEffect, useRef, useMemo } from "react";
import { Box } from "@mui/material";
import { calculateNiceScale, generateYLabelsWithPositions } from "../../PagesContent/BmsFaults/Analytics/ScrollableColumnChart/ChartScaleUtils";
import ScrollableChartWrapper from "../../PagesContent/BmsFaults/Analytics/ScrollableColumnChart/ScrollableChartWrapper";
import RenderChartLegend from "../../PagesContent/AdvancedAnalytics/ContinuousChart/RenderChartLegend";

const CHART_ID      = "auto-resolved-chart";
const CHART_HEIGHT  = 315;
const TOP_OFFSET    = 45;
const BOTTOM_OFFSET = 37;

export const AUTO_RESOLVED_SERIES_NAMES = ["Auto Resolved", "Manually Resolved"];

const AutoResolvedApexChart = ({
  chartData = { categories: [], autoResolvedData: [], manuallyResolvedData: [] },
  isSidebarExpanded = false,
  onBarClick,
}) => {
  const onBarClickRef = useRef(onBarClick);
  useEffect(() => { onBarClickRef.current = onBarClick; }, [onBarClick]);

  const chartDataRef = useRef(chartData);
  useEffect(() => { chartDataRef.current = chartData; }, [chartData]);

  const { series, chartOptions, legendSeries, yLabels, minWidth } = useMemo(() => {
    const {
      autoResolvedData = [],
      manuallyResolvedData = [],
      categories = [],
    } = chartData;

    const maxBarValue = Math.max(...autoResolvedData, ...manuallyResolvedData, 0);
    const MIN_HEIGHT  = maxBarValue > 0 ? maxBarValue * 0.08 : 1;
    const toVisual    = (val) => (val === 0 ? 0 : Math.max(val, MIN_HEIGHT));

    const visualAutoResolved     = autoResolvedData.map(toVisual);
    const visualManuallyResolved = manuallyResolvedData.map(toVisual);

    const visualMax = Math.max(...visualAutoResolved, ...visualManuallyResolved, 0);
    const scale     = calculateNiceScale(0, visualMax, 6);
    const lbls      = generateYLabelsWithPositions(scale, CHART_HEIGHT, TOP_OFFSET, BOTTOM_OFFSET);

    const maxVisible = Math.max(Math.floor(800 / 200), 3);
    const mw         = categories.length * 80;

    const s = [
      { name: "Auto Resolved",     type: "column", data: visualAutoResolved },
      { name: "Manually Resolved", type: "column", data: visualManuallyResolved },
    ];

    const opts = {
      chart: {
        id: CHART_ID,
        type: "bar",
        height: CHART_HEIGHT,
        toolbar: { show: false },
        animations: { enabled: true, dynamicAnimation: { enabled: true, speed: 300 } },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            if (onBarClickRef.current) {
              const label             = chartDataRef.current.categories[config.dataPointIndex] || "";
              const originalDateRange = chartDataRef.current.dateRanges?.[config.dataPointIndex] || "";
              const seriesName        = AUTO_RESOLVED_SERIES_NAMES[config.seriesIndex] || "";
              onBarClickRef.current({ label, originalDateRange, seriesIndex: config.seriesIndex, dataPointIndex: config.dataPointIndex, seriesName });
            }
          },
        },
      },
      stroke:      { width: [0, 0] },
      colors:      ["#008A15", "#B0CC57"],
      plotOptions: { bar: { columnWidth: "24px", borderRadius: 2 } },
      states: {
        hover:  { filter: { type: "lighten", value: 0.1 } },
        active: { allowMultipleDataPointsSelection: false, filter: { type: "none" } },
      },
      xaxis: {
        categories,
        labels:     { style: { colors: "#494949", fontSize: "12px" } },
        axisBorder: { color: "#E0E0E0" },
        axisTicks:  { color: "#E0E0E0" },
        ...(categories.length > maxVisible && { range: maxVisible }),
      },
      yaxis: [
        { seriesName: "Auto Resolved", min: scale.min, max: scale.max, tickAmount: 5, forceNiceScale: false, labels: { formatter: (v) => v.toFixed(0) }, show: false },
        { seriesName: "Auto Resolved", min: scale.min, max: scale.max, tickAmount: 5, forceNiceScale: false, labels: { formatter: (v) => v.toFixed(0) }, show: false },
      ],
      legend:     { show: false },
      grid: {
        show: true,
        borderColor: "#F0F0F0",
        strokeDashArray: 3,
        padding: { left: 0, right: 0, top: 15, bottom: 0 },
        xaxis: { lines: { show: false } },
      },
      dataLabels: { enabled: false },
      tooltip: {
        shared: true,
        intersect: false,
        followCursor: true,
        style: { fontSize: "12px" },
        y: {
          formatter: (val, opts2) => {
            const i  = opts2.dataPointIndex;
            const sv = opts2.seriesIndex;
            const d  = chartDataRef.current;
            if (sv === 0) return (d.autoResolvedData?.[i]     || 0) + " alerts";
            if (sv === 1) return (d.manuallyResolvedData?.[i] || 0) + " alerts";
            return val;
          },
        },
      },
    };

    const legend = [
      { name: "Auto Resolved",     color: "#008A15" },
      { name: "Manually Resolved", color: "#B0CC57" },
    ];

    return { series: s, chartOptions: opts, legendSeries: legend, yLabels: lbls, minWidth: mw };
  }, [chartData]);

  return (
    <Box sx={{
      mt: 3, width: "100%", overflow: "hidden",
      "& .apexcharts-bar-area": { cursor: "pointer" },
      "& .apexcharts-marker": { cursor: "pointer" },
    }}>
      <Box sx={{ mb: 2 }}>
        <RenderChartLegend
          legendSeries={legendSeries}
          chartIDs={[CHART_ID]}
          isLoading={false}
          centered={true}
        />
      </Box>
      <ScrollableChartWrapper
        chartOptions={chartOptions}
        series={series}
        chartType="bar"
        chartHeight={CHART_HEIGHT}
        leftAxis={{ label: "Alert Count", labelData: yLabels }}
        isSidebarExpanded={isSidebarExpanded}
        minWidth={minWidth}
        categoriesCount={chartData.categories?.length || 0}
        drillDownEnabled={false}
      />
    </Box>
  );
};

export default AutoResolvedApexChart;
